import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    CircularProgress,
    Box,
    Typography,
    SelectChangeEvent,
    IconButton,
    Tooltip,
    Link
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import FlashBetSportPromptSummaryDto from '../models/dtos/FlashBetSportPromptSummaryDto';
import FlashBetSportDto from '../models/dtos/FlashBetSportDto';
import { getSportPrompts, getPromptPreviewForQuestionGeneration, getFlashBetSports } from '../Api';
import { extractErrorMessage } from '../utils/ErrorUtil';
import SportAiSettingsModal from './SportAiSettingsModal';

interface PreviewPromptModalProps {
    open: boolean;
    matchId: string;
    sportId: number;
    onClose: () => void;
    onGenerate: (promptId?: string) => void;
    onShowError: (message: string) => void;
}

const PreviewPromptModal: React.FC<PreviewPromptModalProps> = ({
    open,
    matchId,
    sportId,
    onClose,
    onGenerate,
    onShowError
}) => {
    const [prompts, setPrompts] = useState<FlashBetSportPromptSummaryDto[]>([]);
    const [selectedPromptId, setSelectedPromptId] = useState<string>('');
    const [promptPreview, setPromptPreview] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [previewLoading, setPreviewLoading] = useState<boolean>(false);
    const [sport, setSport] = useState<FlashBetSportDto | null>(null);
    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);

    // Load prompts and sport data when modal opens
    useEffect(() => {
        if (open && sportId) {
            loadPrompts();
            loadSport();
        }
    }, [open, sportId]);

    // Load preview when prompt selection changes
    useEffect(() => {
        if (selectedPromptId && matchId) {
            loadPromptPreview(selectedPromptId);
        }
    }, [selectedPromptId, matchId]);

    const loadSport = async () => {
        try {
            const sports = await getFlashBetSports();
            const foundSport = sports.find(s => s.id === sportId);
            if (foundSport) {
                setSport(foundSport);
            }
        } catch (error) {
            console.error('Failed to load sport:', error);
            const errorMsg = await extractErrorMessage(error, 'Failed to load sport');
            onShowError(errorMsg);
        }
    };

    const loadPrompts = async () => {
        try {
            setLoading(true);
            const promptsList = await getSportPrompts(sportId);
            setPrompts(promptsList);
            
            // Auto-select the active prompt
            const activePrompt = promptsList.find(p => p.isActive);
            if (activePrompt) {
                setSelectedPromptId(activePrompt.id);
            } else if (promptsList.length > 0) {
                setSelectedPromptId(promptsList[0].id);
            }
        } catch (error) {
            console.error('Failed to load prompts:', error);
            const errorMsg = await extractErrorMessage(error, 'Failed to load prompts');
            onShowError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const loadPromptPreview = async (promptId: string) => {
        try {
            setPreviewLoading(true);
            const preview = await getPromptPreviewForQuestionGeneration(matchId, promptId);
            setPromptPreview(preview);
        } catch (error) {
            console.error('Failed to load prompt preview:', error);
            const errorMsg = await extractErrorMessage(error, 'Failed to load prompt preview');
            onShowError(errorMsg);
            setPromptPreview('Error loading preview');
        } finally {
            setPreviewLoading(false);
        }
    };

    const selectedPrompt = prompts.find(p => p.id === selectedPromptId);

    const handlePromptChange = (event: SelectChangeEvent<string>) => {
        setSelectedPromptId(event.target.value);
    };

    const handleEditPrompt = () => {
        setSettingsModalOpen(true);
    };

    const handleSettingsModalClose = () => {
        setSettingsModalOpen(false);
        // Reload prompts and preview after closing settings modal to reflect any changes
        loadPrompts();
        if (selectedPromptId && matchId) {
            loadPromptPreview(selectedPromptId);
        }
    };

    const handleGenerate = () => {
        onGenerate(selectedPromptId || undefined);
        handleClose();
    };

    const handleClose = () => {
        setPromptPreview('');
        setSelectedPromptId('');
        setSport(null);
        onClose();
    };

    return (
        <>
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Preview & Generate Questions</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel id="prompt-select-label">Prompt</InputLabel>
                                <Select
                                    labelId="prompt-select-label"
                                    id="prompt-select"
                                    value={selectedPromptId}
                                    label="Prompt"
                                    onChange={handlePromptChange}
                                >
                                    {prompts.map((prompt) => (
                                        <MenuItem key={prompt.id} value={prompt.id}>
                                            {prompt.name} {prompt.isActive ? '(Active)' : ''} {prompt.isDefault ? '(Default)' : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Tooltip title="Prompt variants">
                                <IconButton 
                                    onClick={handleEditPrompt}
                                    disabled={!selectedPromptId}
                                    color="primary"
                                    sx={{ 
                                        border: 1, 
                                        borderColor: 'divider',
                                        borderRadius: 1
                                    }}
                                >
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {selectedPrompt && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Questions are generated by the AI hub task{' '}
                                <Link component={RouterLink} to={`/ai/tasks/${encodeURIComponent(selectedPrompt.questionTaskCode)}`}
                                      target="_blank" rel="noopener" sx={{ fontFamily: 'monospace' }}>
                                    {selectedPrompt.questionTaskCode}
                                </Link>
                                {' '}— its prompt, model and cost are in AI › Tasks.
                            </Typography>
                        )}

                        <Typography variant="subtitle2" gutterBottom>
                            Prompt Preview:
                        </Typography>
                        
                        {previewLoading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                                <CircularProgress size={30} />
                            </Box>
                        ) : (
                            <TextField
                                fullWidth
                                multiline
                                rows={30}
                                value={promptPreview}
                                InputProps={{
                                    readOnly: true,
                                    style: { fontFamily: 'monospace', fontSize: '0.85rem' }
                                }}
                                variant="outlined"
                                sx={{ 
                                    '& .MuiInputBase-root': {
                                        backgroundColor: '#f5f5f5'
                                    }
                                }}
                            />
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    Close
                </Button>
                <Button 
                    onClick={handleGenerate} 
                    variant="contained" 
                    color="primary"
                    disabled={!selectedPromptId || previewLoading}
                >
                    Generate
                </Button>
            </DialogActions>
        </Dialog>

        <SportAiSettingsModal
            open={settingsModalOpen}
            sport={sport}
            onClose={handleSettingsModalClose}
            initialPromptId={selectedPromptId}
            saveAndClose={true}
        />
    </>
    );
};

export default PreviewPromptModal;
